import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pipeline, PipelineStage, Deal, pipelineAPI, PaymentStatus, ShippingStatus } from '../../services/api';
import {
  PlusIcon,
  FunnelIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon,
  TruckIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { Badge } from '../ui';
import DealStageModal from './DealStageModal';

// Helper functions for status colors
const getPaymentStatusColor = (status?: PaymentStatus): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
  switch (status) {
    case 'PAID': return 'success';
    case 'PARTIAL': return 'warning';
    case 'PENDING': return 'primary';
    case 'OVERDUE': return 'error';
    case 'REFUNDED': return 'neutral';
    case 'DISPUTED': return 'error';
    case 'CANCELLED': return 'neutral';
    default: return 'neutral';
  }
};

const getShippingStatusColor = (status?: ShippingStatus): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
  switch (status) {
    case 'DELIVERED': return 'success';
    case 'IN_TRANSIT': return 'primary';
    case 'OUT_FOR_DELIVERY': return 'primary';
    case 'PICKED_UP': return 'primary';
    case 'LABEL_CREATED': return 'warning';
    case 'NOT_SHIPPED': return 'neutral';
    case 'RETURNED': return 'error';
    case 'EXCEPTION': return 'error';
    default: return 'neutral';
  }
};

const formatStatus = (status?: string): string => {
  if (!status) return '';
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

interface PipelineBoardProps {
  embedded?: boolean;
}

const PipelineBoard: React.FC<PipelineBoardProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [boardData, setBoardData] = useState<any>({});
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  useEffect(() => {
    loadPipelines();
  }, []);

  useEffect(() => {
    if (selectedPipeline) {
      loadBoardData();
      loadStages();
    }
  }, [selectedPipeline]);

  const loadPipelines = async () => {
    try {
      const data = await pipelineAPI.getAll();
      const activePipelines = data.filter(p => p.isActive);
      setPipelines(activePipelines);
      
      // Select default or first pipeline
      const defaultPipeline = activePipelines.find(p => p.isDefault) || activePipelines[0];
      setSelectedPipeline(defaultPipeline);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load pipelines');
    }
  };

  const loadBoardData = async () => {
    if (!selectedPipeline) return;
    
    try {
      setLoading(true);
      const data = await pipelineAPI.getBoardView(selectedPipeline.id);
      setBoardData(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load board data');
    } finally {
      setLoading(false);
    }
  };

  const loadStages = async () => {
    if (!selectedPipeline) return;
    
    try {
      const data = await pipelineAPI.getStages(selectedPipeline.id);
      setStages(data);
    } catch (err: any) {
      console.error('Failed to load stages:', err);
    }
  };

  const handleDealClick = (deal: Deal) => {
    navigate(`/deals/${deal.id}`);
  };

  const handleMoveDeal = (deal: Deal) => {
    setSelectedDeal(deal);
    setShowStageModal(true);
  };

  const handleStageModalClose = () => {
    setShowStageModal(false);
    setSelectedDeal(null);
    loadBoardData();
  };

  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount));
  };

  const calculateStageTotal = (stageDeals: Deal[]) => {
    return stageDeals.reduce((sum, deal) => sum + Number(deal.amount), 0);
  };

  const calculateWeightedTotal = (stageDeals: Deal[]) => {
    return stageDeals.reduce((sum, deal) => {
      return sum + (Number(deal.amount) * (deal.probability || 0) / 100);
    }, 0);
  };

  if (loading && !selectedPipeline) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header - hidden when embedded */}
      {!embedded && (
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <FunnelIcon className="h-8 w-8 text-neutral-400" />
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Pipeline Board</h1>
              <p className="text-sm text-neutral-500">Visualize and manage your deals</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={selectedPipeline?.id || ''}
              onChange={(e) => {
                const pipeline = pipelines.find(p => p.id === parseInt(e.target.value));
                setSelectedPipeline(pipeline || null);
              }}
              className="block w-64 pl-3 pr-10 py-2 text-base border-neutral-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => navigate('/deals/new')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              New Deal
            </button>
          </div>
        </div>
      )}

      {/* Pipeline selector when embedded */}
      {embedded && (
        <div className="flex items-center gap-4">
          <select
            value={selectedPipeline?.id || ''}
            onChange={(e) => {
              const pipeline = pipelines.find(p => p.id === parseInt(e.target.value));
              setSelectedPipeline(pipeline || null);
            }}
            className="block w-64 pl-3 pr-10 py-2 text-base border-neutral-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            {pipelines.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Board */}
      <div className="overflow-x-auto pb-4">
        <div className="inline-flex space-x-4 min-w-full">
          {stages.map((stage) => {
            const stageDeals = boardData[stage.id]?.deals || [];
            const stageTotal = calculateStageTotal(stageDeals);
            const weightedTotal = calculateWeightedTotal(stageDeals);

            return (
              <div
                key={stage.id}
                className="flex-shrink-0 w-80 bg-neutral-50 rounded-lg"
              >
                {/* Stage Header */}
                <div className="px-4 py-3 border-b border-neutral-200 bg-white rounded-t-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {stage.color && (
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: stage.color }}
                        />
                      )}
                      <h3 className="text-sm font-semibold text-neutral-900">
                        {stage.name}
                      </h3>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-800">
                        {stageDeals.length}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-neutral-500 space-y-1">
                    <div>Total: {formatCurrency(stageTotal)}</div>
                    <div>Weighted: {formatCurrency(weightedTotal)}</div>
                  </div>
                </div>

                {/* Deals List */}
                <div className="p-3 space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {stageDeals.length === 0 ? (
                    <div className="text-center py-8 text-neutral-400 text-sm">
                      No deals in this stage
                    </div>
                  ) : (
                    stageDeals.map((deal: Deal) => (
                      <div
                        key={deal.id}
                        className="bg-white rounded-lg shadow-sm border border-neutral-200 p-3 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => handleDealClick(deal)}
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {deal.orderNumber && (
                                <span className="text-xs font-mono text-neutral-500 block mb-0.5">
                                  {deal.orderNumber}
                                </span>
                              )}
                              <h4 className="text-sm font-medium text-neutral-900 line-clamp-2">
                                {deal.description}
                              </h4>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveDeal(deal);
                              }}
                              className="text-neutral-400 hover:text-primary-600 ml-2"
                              title="Move to another stage"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                            </button>
                          </div>

                          <div className="flex items-center justify-between text-xs">
                            <span className="font-semibold text-primary-600">
                              {formatCurrency(deal.amount)}
                            </span>
                            {deal.probability !== undefined && (
                              <span className="text-neutral-500">
                                {deal.probability}% prob.
                              </span>
                            )}
                          </div>

                          {/* Payment & Shipping Status */}
                          {(deal.paymentStatus || deal.shippingStatus) && (
                            <div className="flex flex-wrap gap-1">
                              {deal.paymentStatus && (
                                <Badge variant={getPaymentStatusColor(deal.paymentStatus)} size="sm" icon={<BanknotesIcon className="h-3 w-3" />}>
                                  {formatStatus(deal.paymentStatus)}
                                </Badge>
                              )}
                              {deal.shippingStatus && (
                                <Badge variant={getShippingStatusColor(deal.shippingStatus)} size="sm" icon={<TruckIcon className="h-3 w-3" />}>
                                  {formatStatus(deal.shippingStatus)}
                                </Badge>
                              )}
                            </div>
                          )}

                          {deal.client && (
                            <div className="flex items-center text-xs text-neutral-600">
                              <UserIcon className="h-3 w-3 mr-1" />
                              <span className="truncate">{deal.client.name}</span>
                            </div>
                          )}

                          {deal.closeDate && (
                            <div className="flex items-center text-xs text-neutral-500">
                              <CalendarIcon className="h-3 w-3 mr-1" />
                              <span>
                                {new Date(deal.closeDate).toLocaleDateString()}
                              </span>
                            </div>
                          )}

                          {deal.user && (
                            <div className="flex items-center text-xs text-neutral-500">
                              <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center mr-1">
                                <span className="text-primary-600 font-medium text-xs">
                                  {deal.user.firstName?.[0] || deal.user.email[0].toUpperCase()}
                                </span>
                              </div>
                              <span className="truncate">
                                {deal.user.firstName || deal.user.email}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {showStageModal && selectedDeal && (
        <DealStageModal
          deal={selectedDeal}
          stages={stages}
          onClose={handleStageModalClose}
        />
      )}
    </div>
  );
};

export default PipelineBoard;

